import { useRef, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { useHorizontalResize } from 'hooks/useHorizontalResize'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import * as Styled from '../styled'
import { IAudioItem } from 'types/audio'
import { useAudioItemResize } from '../useAudioItemResize'
import Text from 'common-components/Text'
import AudioWave from '../AudioWave'
import { ttsSelectors } from 'store/features/tts/selectors'
import {
  getAudioItemBackgroundColor,
  getIsAudioDecodeAllowed
} from '../helpers'
import { useGetTtsItemGeneratedState } from 'containers/SubSidebar/TtsContent/hooks'
import { TRACK_ITEM_CLASS_NAME } from 'consts'
import { useTimelineContext } from 'context/TimelineContext'
import { secondsToPixels } from 'utils/timeline'
import { editorSelectors } from 'store/features/editor/selectors'
import { isBlobUrl } from 'utils'

import { COLORS } from 'consts/colors'

import { TRootState } from 'store/types'

interface ITrackItemProps {
  data: IAudioItem
  isActive: boolean
  isOverlappingPlayHead: boolean
  onClick: (event: React.MouseEvent<HTMLDivElement>) => void
  updateAudioItems: (audioItems: IAudioItem[]) => void
  isResizeBtnsVisible?: boolean
  selectedBorderColor: string
  selectedBorderSize: number
}

const TrackItem = ({
  data,
  isActive,
  onClick,
  updateAudioItems,
  isResizeBtnsVisible,
  selectedBorderColor,
  selectedBorderSize
}: ITrackItemProps) => {
  const ref = useRef<HTMLDivElement>(null)

  const metaData = useSelector((state: TRootState) =>
    projectDataSelectors.soundMetaDataByAssetId(state, data.assetId)
  )

  const fileSize = metaData?.fileSize
  const isDraftAudio = !useGetTtsItemGeneratedState(Number(data.assetId))
  const timelineRulerSliceDuration = useSelector(
    editorSelectors.timelineRulerSliceDuration
  )
  const audioItems = useSelector(projectDataSelectors.multilineAudios)
  const isAudioItemActionsDisabled = useSelector(
    ttsSelectors.isGenerationInProcess
  )

  const { scrollableContainerRef } = useTimelineContext()

  const backgroundColor = useMemo(() => {
    return getAudioItemBackgroundColor(data.type, isDraftAudio)
  }, [data.type, isDraftAudio])

  const isAudioDecodeAllowed = getIsAudioDecodeAllowed(data, fileSize)

  const isTitleVisible = !isActive || !isAudioDecodeAllowed
  const { onResizeStart, onResizeEnd, handleLeftResize, handleRightResize } =
    useAudioItemResize({
      itemId: data.id,
      lineIndex: data.lineIndex,
      audioItems,
      updateAudioItems
    })

  const { onLeftResize, onRightResize } = useHorizontalResize({
    ref,
    onResizeEnd,
    onResizeStart,
    handleLeftResize,
    handleRightResize,
    scrollableContainerRef,
    left: secondsToPixels(data.projectStart, timelineRulerSliceDuration)
  })

  const isBlobPath = useMemo(
    () => data?.mediaPath && isBlobUrl(data.mediaPath),
    [data?.mediaPath]
  )

  const finalTitle = useMemo(() => {
    const regExp =
      /(\.(mp3|wav|ogg|oga|opus|flac|m4a|weba|aiff|alac|caf|amr)$)/i
    if (regExp.test(data.title)) {
      return data.title
    }
    return `${data.title}.mp3`
  }, [data.title])

  return (
    <Styled.TrackItem
      $isActive={isActive}
      $isDisabled={isAudioItemActionsDisabled}
      onClick={onClick}
      ref={ref}
      id={data.id}
      $backgroundColor={backgroundColor}
      className={TRACK_ITEM_CLASS_NAME}
      data-type={data.type}
      data-asset-id={data.assetId}
    >
      <Styled.TrackItemWrapper
        isActive={isActive}
        onLeftResize={onLeftResize}
        onRightResize={onRightResize}
        isResizeBtnsVisible={isResizeBtnsVisible}
        selectedBorderColor={selectedBorderColor}
        selectedBorderSize={selectedBorderSize}
      >
        {isTitleVisible ? (
          <Styled.AudioNameWrapper>
            <Text type="h5" color="white" ellipsis>
              {finalTitle}
            </Text>
            {isBlobPath && (
              <Styled.BlobLoader
                size={16}
                wrapperSize={20}
                loaderColor={COLORS.white}
              />
            )}
          </Styled.AudioNameWrapper>
        ) : (
          isAudioDecodeAllowed && (
            <AudioWave
              audioItem={data}
              isActive={isActive}
              isDraft={isDraftAudio}
              fileSize={fileSize}
            />
          )
        )}
      </Styled.TrackItemWrapper>
    </Styled.TrackItem>
  )
}

export default TrackItem
